import { attackIdFor } from './attack-ids'
import { newEvidence, newHunt, newTrailStep, type Hunt } from './hunt-model'
import type { HuntStore } from './hunt-store'
import { entityKey, type Signal, type SignalTriage } from './signal-model'

/**
 * "Open as hunt" (decision S2, tier 2): an investigation started from a
 * signal is a hunt with a pre-filled hypothesis, the signal's ATT&CK refs, a
 * window around the signal, and three seeded trail steps — the raw-event
 * query, the same-entity SIGNALS query, and the entity's source-stream
 * activity. The signal itself, its pinned rows and notes become evidence.
 *
 * Pure builder + one store-driving function so the page stays thin and the
 * seeding is testable without React.
 */

export interface HuntSeed {
  hunt: Hunt
  steps: string[]
  evidence: ReturnType<typeof newEvidence>[]
}

function quote(v: string): string {
  return `'${v.replace(/'/g, "''")}'`
}

/** `stream=SIGNALS where suspectuser = 'x' OR targethost = 'y' | duration 7d | limit 200` */
export function sameEntitySignalsQuery(signal: Signal, window = '7d'): string {
  const conds = signal.entities.map((e) => `${e.role.toLowerCase()}${e.field.toLowerCase()} = ${quote(e.value)}`)
  const where = conds.length ? ` where ${conds.join(' OR ')}` : ''
  return `stream=SIGNALS${where} | duration ${window} | limit 200`
}

/** The entity's activity on the detection's first source stream. */
export function entityActivityQuery(signal: Signal, window = '1d'): string | null {
  const stream = signal.streams[0]
  if (!stream) return null
  const host = signal.entities.find((e) => e.field.toLowerCase() === 'host')
  const user = signal.entities.find((e) => e.field.toLowerCase() === 'user')
  const ip = signal.entities.find((e) => e.field.toLowerCase() === 'ip')
  const conds: string[] = []
  if (host) conds.push(`System = ${quote(host.value)}`)
  if (user) conds.push(`User = ${quote(user.value)}`)
  if (ip) conds.push(`SrcIP = ${quote(ip.value)}`)
  const where = conds.length ? ` where ${conds.join(' OR ')}` : ''
  return `stream=${stream}${where} | duration ${window} | limit 200`
}

export function buildHuntSeed(signal: Signal, triage: SignalTriage | null, assignee: string | null): HuntSeed {
  const anchor = signal.entities[0]
  const name = `${signal.detectionName || 'Signal'}${anchor ? ` — ${anchor.value}` : ''}`
  const hypothesis = `${signal.detectionName || 'A detection'} fired${anchor ? ` on ${anchor.field.toLowerCase()} ${anchor.value}` : ''} — confirm or refute that this is ${signal.severity === 'critical' || signal.severity === 'high' ? 'an active intrusion' : 'malicious activity'}.`
  const attack = [signal.technique, signal.subtechnique]
    .filter(Boolean)
    .map((t) => ({ technique: t, techniqueId: attackIdFor(t) }))
  const hunt = newHunt({
    name,
    hypothesis,
    methodology: 'entity-pivot',
    attack,
    scope: { streams: [...new Set(['SIGNALS', ...signal.streams])], window: '7d' },
    assignee,
  })
  const steps = [signal.rawEventQuery, sameEntitySignalsQuery(signal), entityActivityQuery(signal)].filter(
    (q): q is string => !!q,
  )
  const excerpt: Record<string, string> = {
    uuid: signal.uuid,
    detection: signal.detectionName,
    severity: signal.severity,
    tactic: signal.tactic,
    technique: signal.technique,
    at: new Date(signal.at).toISOString(),
  }
  for (const e of signal.entities) excerpt[`${e.role.toLowerCase()} ${e.field.toLowerCase()}`] = e.value
  const evidence = [
    newEvidence({
      kind: 'event',
      title: `Signal ${signal.uuid} — ${signal.detectionName}`,
      sourceQuery: sameEntitySignalsQuery(signal),
      excerpt,
      note: 'Origin signal of this hunt.',
    }),
    ...(triage?.pins ?? []).map((p) =>
      newEvidence({ kind: 'event', title: `Pinned raw event`, sourceQuery: p.sourceQuery, excerpt: p.excerpt }),
    ),
    ...(triage?.notes ?? []).map((n) =>
      newEvidence({ kind: 'note', title: `Note by ${n.who}`, note: n.text }),
    ),
  ]
  return { hunt, steps, evidence }
}

/**
 * Creates the hunt, opens a session titled from the signal, records the seed
 * queries as unrun trail steps (hitCount null — the cockpit runs them), and
 * pins the evidence. Returns the hunt id for navigation.
 */
export function openSignalAsHunt(
  store: HuntStore,
  signal: Signal,
  triage: SignalTriage | null,
  assignee: string | null,
): string {
  const seed = buildHuntSeed(signal, triage, assignee)
  store.create(seed.hunt)
  store.activate(seed.hunt.id)
  const session = store.startSession(seed.hunt.id, `From signal ${signal.uuid}`)
  if (session) {
    for (const q of seed.steps) {
      store.appendTrailStep(seed.hunt.id, session.id, newTrailStep({ query: q, hitCount: null, durationMs: null }))
    }
  }
  for (const e of seed.evidence) store.pinEvidence(seed.hunt.id, e)
  return seed.hunt.id
}

/** Same-entity signals — the panel's fallback when no storyline holds the signal. */
export function relatedSignals(signal: Signal, all: Signal[]): Signal[] {
  const keys = new Set(signal.entities.map(entityKey))
  return all
    .filter((s) => s.uuid !== signal.uuid && s.entities.some((e) => keys.has(entityKey(e))))
    .sort((a, b) => b.at - a.at)
}
